<template>
  <v-card outlined>
    <v-card-title
      class="d-flex align-center flex-wrap pb-3"
      style="gap: 8px;">
      <div>
        <span class="text-subtitle-1 font-weight-medium">
          {{ selectedFactoryName ? selectedFactoryName + ' — Products' : 'All products' }}
        </span>
        <span class="text-caption grey--text ml-2">{{ products.length }} items</span>
      </div>
      <v-spacer />
      <v-btn
        v-if="selectedFactoryId"
        text
        x-small
        class="text-none"
        @click="$emit('clear-factory')">
        Show all factories
      </v-btn>
    </v-card-title>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      height="2" />

    <v-data-table
      :headers="headers"
      :items="filteredProducts"
      :items-per-page="15"
      :footer-props="{ 'items-per-page-options': [15, 25, 50] }"
      :sort-by="['saleDate']"
      :sort-desc="[true]"
      dense
      class="text-body-2">
      <template #item.fittingImage="{ item }">
        <v-avatar
          tile
          size="36"
          class="my-1"
          style="border-radius: 4px;">
          <v-img
            v-if="item.fittingImage"
            :src="$convertImgix(`${item.fittingImage}?w=120`)"
            :alt="item.product"
            style="cursor: pointer"
            @click="openPreview(item.fittingImage)" />
          <v-img
            v-else
            :src="noImgAvailable"
            :alt="item.product" />
        </v-avatar>
      </template>

      <template #item.status="{ item }">
        <v-chip
          v-if="mode === 'tentative'"
          x-small
          :color="item.status === 'manufacturing' ? 'green lighten-5' : 'blue lighten-5'"
          :text-color="item.status === 'manufacturing' ? 'green darken-3' : 'blue darken-3'">
          {{ item.status === 'manufacturing' ? 'Manufacturing' : 'Order' }}
        </v-chip>
        <v-chip
          v-else
          x-small
          :color="item.allocationType === 'Actual' ? 'green lighten-5' : 'blue lighten-5'"
          :text-color="item.allocationType === 'Actual' ? 'green darken-3' : 'blue darken-3'">
          {{ item.allocationType === 'Actual' ? 'มี PO แล้ว' : 'ยังไม่มี PO' }}
        </v-chip>
      </template>

      <template #item.collection="{ item }">
        <div class="d-flex flex-column">
          <span class="font-weight-medium">
            {{ item.collection ?? '—' }}
          </span>
          <span class="text-caption grey--text">
            {{ item.product || '—' }}
          </span>
        </div>
      </template>

      <template #item.saleDate="{ item }">
        {{ item.saleDate | dateUserFormat }}
      </template>

      <template #item.amount="{ item }">
        <span class="font-weight-medium">{{ item.amount != null ? item.amount.toLocaleString() : '—' }}</span>
      </template>

      <template #no-data>
        <div class="text-center grey--text py-8">
          <v-icon
            large
            color="grey lighten-1">
            mdi-package-variant
          </v-icon>
          <div class="mt-2">
            No products found for this period
          </div>
        </div>
      </template>
    </v-data-table>

    <v-dialog
      v-model="previewDialog"
      max-width="640">
      <v-card class="po-preview">
        <div class="po-preview__head">
          <v-btn
            icon
            small
            @click="previewDialog = false">
            <v-icon size="18">
              mdi-close
            </v-icon>
          </v-btn>
        </div>
        <img
          :src="previewURL ? $convertImgix(previewURL) : noImgAvailable"
          class="po-preview__img"
          alt="preview">
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import NoImgAvailable from '@/assets/image/no_picture_available.png'

export default {
  name: 'ProductTable',
  props: {
    products: { type: Array, default: () => [] },
    mode: { type: String, default: 'tentative' },
    loading: { type: Boolean, default: false },
    selectedFactoryId: { type: Number, default: null }
  },
  data () {
    return {
      headers: [
        { text: '', value: 'fittingImage', sortable: false, width: '52px' },
        // { text: 'Product', value: 'product', sortable: true },
        { text: 'Collection', value: 'collection', sortable: true },
        { text: 'Factory', value: 'factory.name', sortable: true },
        { text: 'Sale Date', value: 'saleDate', sortable: true },
        { text: 'Amount (SKU)', value: 'amount', sortable: true, align: 'right' },
        // { text: 'PO', value: 'po', sortable: false },
        { text: 'Status', value: 'status', sortable: false }
      ],
      noImgAvailable: NoImgAvailable,
      previewDialog: false,
      previewURL: ''
    }
  },
  computed: {
    filteredProducts () {
      if (!this.selectedFactoryId) return this.products
      return this.products.filter((p) => p.factory && p.factory.id === this.selectedFactoryId)
    },
    selectedFactoryName () {
      if (!this.selectedFactoryId) return null
      const product = this.products.find((p) => p.factory && p.factory.id === this.selectedFactoryId)
      return product ? product.factory.name : `Factory #${this.selectedFactoryId}`
    }
  },
  methods: {
    openPreview (url) {
      if (!url) {
        return
      }
      this.previewURL = url
      this.previewDialog = true
    }
  }
}
</script>

<style scoped>
.po-preview {
  overflow: hidden;
  position: relative;
  border-radius: 8px;
}
.po-preview__head {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: flex-end;
  padding: 8px;
}
.po-preview__img {
  display: block;
  width: 100%;
  max-height: 80vh;
  object-fit: contain;
}
</style>
